import { AfterViewInit, Component, HostListener, OnDestroy, inject } from '@angular/core';
import { Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { AuthStateService } from '../../../core/services/auth-state.service';
import { AuthService } from '../../../core/services/auth.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { ActiveTabsService } from '../../../core/services/active-tabs.service';
import { BreadcrumbService } from '../../../core/services/breadcrumb.service';
import { ModalInteractionManager } from '../../utils/modal-interaction-manager';

@Component({
    selector: 'app-full-layout',
    imports: [RouterOutlet, RouterLink, RouterLinkActive, AsyncPipe],
    templateUrl: './full-layout.component.html'
})
export class FullLayoutComponent implements AfterViewInit, OnDestroy {
  private authState = inject(AuthStateService);
  private authService = inject(AuthService);
  private router = inject(Router);
  readonly pageTitleService = inject(PageTitleService);
  readonly activeTabsService = inject(ActiveTabsService);
  readonly breadcrumbService = inject(BreadcrumbService);
  private modalObserver?: MutationObserver;
  private modalStateSyncQueued = false;
  private readonly modalInteractions = new ModalInteractionManager();

  @HostListener('document:keydown', ['$event'])
  preventInputEnterSubmit(event: KeyboardEvent): void {
    if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
      event.preventDefault();
    }
  }

  private readonly handleModalDismissClick = (event: MouseEvent): void => {
    const modal = this.findDismissTargetModal(event.target as Element | null);
    if (!modal) return;
    if (modal.getAttribute('data-backdrop') === 'static' || modal.getAttribute('aria-busy') === 'true') return;
    window.setTimeout(() => {
      if (!document.body.contains(modal)) {
        this.queueModalStateSync();
        return;
      }

      const closeButton = modal?.querySelector<HTMLElement>(
        '.modal-header button.close, button.close, [data-dismiss="modal"], .modal-footer .bg-close'
      );
      if (closeButton && !closeButton.matches(':disabled')) closeButton.click();
      this.queueModalStateSync();
    }, 0);
  };

  ngAfterViewInit(): void {
    this.modalInteractions.start();
    this.modalObserver = new MutationObserver(() => this.queueModalStateSync());
    this.modalObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });
    document.addEventListener('click', this.handleModalDismissClick);
    this.syncModalState();
  }

  ngOnDestroy(): void {
    this.modalInteractions.stop();
    this.modalObserver?.disconnect();
    document.removeEventListener('click', this.handleModalDismissClick);
    document.body.classList.remove('modal-open');
  }

  get userPermission(): number {
    return this.authState.currentUser?.role ?? 99;
  }

  goBack(): void {
    history.back();
  }

  goMember(): void {
    void this.router.navigate(['/member']);
  }

  toggleSidebar(): void {
    document.body.classList.toggle('sidebar-hidden');
    if (document.body.classList.contains('sidebar-hidden')) {
      document.getElementById('side-nav-backdrop')?.remove();
    } else {
      const backdrop = document.createElement('div');
      backdrop.id = 'side-nav-backdrop';
      backdrop.className = 'modal-backdrop fade';
      backdrop.addEventListener('click', () => this.closeSidebar());
      document.body.appendChild(backdrop);
    }
  }

  closeSidebar(): void {
    document.body.classList.add('sidebar-hidden');
    document.getElementById('side-nav-backdrop')?.remove();
  }

  sessionClear(): void {
    // Search conditions belong to each page; the selected studio belongs to the session.
    const retained = new Set(['ngStorage-currentUser', 'selected_studio_id', 'select.studio', 'ngStorage-studio']);
    Object.keys(sessionStorage).forEach(key => {
      if (!retained.has(key) && !key.startsWith('sfRecovery:')) sessionStorage.removeItem(key);
    });
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }

  private queueModalStateSync(): void {
    if (this.modalStateSyncQueued) return;
    this.modalStateSyncQueued = true;
    window.requestAnimationFrame(() => {
      this.modalStateSyncQueued = false;
      this.syncModalState();
    });
  }

  private syncModalState(): void {
    const hasOpenModal = document.querySelector('.modal.show, .modal.d-block') !== null;
    document.body.classList.toggle('modal-open', hasOpenModal);
  }

  private findDismissTargetModal(target: Element | null): HTMLElement | null {
    if (!target) return null;
    if (target instanceof HTMLElement && target.classList.contains('modal')) {
      return target.classList.contains('show') || target.classList.contains('d-block') ? target : null;
    }

    const backdrop = target.closest('.modal-backdrop.fade.show') as HTMLElement | null;
    if (!backdrop || backdrop.id === 'side-nav-backdrop') return null;
    return this.findModalForBackdrop(backdrop);
  }

  private findModalForBackdrop(backdrop: HTMLElement): HTMLElement | null {
    let sibling = backdrop.nextElementSibling;
    while (sibling) {
      if (sibling.classList.contains('modal')) return sibling as HTMLElement;
      sibling = sibling.nextElementSibling;
    }
    const modals = Array.from(document.querySelectorAll<HTMLElement>('.modal.show, .modal.d-block'));
    return modals.at(-1) ?? null;
  }
}
